import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, config } from "../lib/api.js";
import { PageHeader } from "../components/PageHeader.jsx";
import { EmptyState } from "../components/EmptyState.jsx";

export function Competitors() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["competitors"], queryFn: api.competitors, enabled: config.hasBase });
  const recent = useQuery({ queryKey: ["competitor-posts"], queryFn: () => api.competitorPosts(200), enabled: config.hasBase });
  const [form, setForm] = useState({ profileUrl: "", displayName: "" });
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState(false);

  const add = useMutation({
    mutationFn: api.addCompetitor,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["competitors"] })
  });
  const remove = useMutation({
    mutationFn: api.removeCompetitor,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["competitors"] })
  });

  if (!config.hasBase) return <EmptyState title="Backend not configured" description="Set VITE_API_BASE / VITE_API_TOKEN." />;

  async function runDigest() {
    setBusy(true);
    try { setDigest(await api.competitorDigest()); }
    catch (e) { setDigest({ error: e.message || "failed" }); }
    finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader
        title="Competitors"
        subtitle="Track up to 10 profiles. Posts are captured when you visit their pages with the extension installed."
        actions={<button className="btn btn-primary" disabled={busy} onClick={runDigest}>{busy ? "Generating…" : "AI competitor digest"}</button>}
      />

      <div className="card mb-4">
        <div className="font-medium mb-3">Add a competitor</div>
        <form
          className="grid grid-cols-1 md:grid-cols-3 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate(form, {
              onSuccess: () => setForm({ profileUrl: "", displayName: "" })
            });
          }}
        >
          <input className="input md:col-span-2" placeholder="https://www.linkedin.com/in/<handle>"
            value={form.profileUrl} onChange={(e) => setForm({ ...form, profileUrl: e.target.value })} required />
          <div className="flex gap-2">
            <input className="input flex-1" placeholder="Display name"
              value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required />
            <button className="btn btn-primary" type="submit" disabled={add.isPending}>Add</button>
          </div>
        </form>
        {add.error && <div className="text-rose-400 text-xs mt-2">{add.error.message}</div>}
      </div>

      {digest && (
        <div className="card mb-4">
          <div className="font-medium mb-2">AI digest (last 24h)</div>
          {digest.error ? (
            <div className="text-rose-400 text-sm">{digest.error}</div>
          ) : (
            <>
              <div className="text-sm mb-3">{digest.summary}</div>
              <ul className="space-y-2">
                {digest.items?.map((it, i) => (
                  <li key={i} className="text-sm">
                    <div className="font-medium">{it.competitorName}</div>
                    <div className="subtle">{it.highlight}</div>
                    {it.gap && <div className="text-emerald-400 text-xs mt-1">Gap to exploit: {it.gap}</div>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="font-medium mb-2">Tracked</div>
          {list.isLoading ? <div className="subtle">Loading…</div> : !list.data?.competitors?.length ? (
            <div className="subtle text-sm">None yet.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {list.data.competitors.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="font-medium">{c.display_name}</div>
                    <a className="text-xs subtle" href={c.profile_url} target="_blank" rel="noreferrer noopener">{c.profile_url}</a>
                  </div>
                  <button className="btn btn-ghost text-xs" onClick={() => remove.mutate(c.id)} disabled={remove.isPending}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <div className="font-medium mb-2">Recent competitor posts captured</div>
          {recent.isLoading ? <div className="subtle">Loading…</div> : !recent.data?.posts?.length ? (
            <div className="subtle text-sm">Visit a competitor&apos;s profile to capture their posts.</div>
          ) : (
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
              {recent.data.posts.map((p) => (
                <li key={p.urn} className="text-sm border-b border-white/5 pb-2">
                  <div className="font-medium">{p.author_name}</div>
                  <div className="line-clamp-3 subtle">{p.body}</div>
                  <div className="text-xs subtle mt-1">{p.like_count} likes · {p.comment_count} comments · {p.repost_count} reposts</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
